import { inject, Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { firstValueFrom, map, Observable } from 'rxjs';
import { DeleteService } from 'src/app/core/delete-confirmation/delete-confirmation.service';
import { ClassJaxService } from 'src/app/core/services/classjax.service';
import { ApiListResponse, ApiSingleResponse } from 'src/app/core/services/api.types';
import { AnalyticsService } from 'src/app/core/services/analytics.service';
import { AuthService } from 'src/app/subscription/services/auth/auth.service';
import {
  Org,
  OrgAdmin,
  OrgCollaborator,
  OrgEntry,
  OrgStaff,
  OrgStudent,
  OrgWithMembership,
  OrgCreditBalance,
  UserDetail,
  Campus,
  CampusEntry,
  TeamSummary,
  CampusWithDetails,
  CampusStaff,
  CreateCampusRequest,
  UpdateCampusRequest,
  AddTeamMemberRequest,
  UpdateTeamMemberRequest,
  UpdateOrgRequest,
  TeamPage,
  CampusPageSummary,
  CreateCampusPageRequest,
  UpdateCampusPageRequest,
  AssumeAuditEntry,
} from '../models/org.model';
import { CampusPlannersResponse } from '../../campuses/campus-planners/campus-planners.model';

@Injectable({
  providedIn: 'root',
})
export class OrgService {
  private api = inject(ClassJaxService);
  private analyticsService = inject(AnalyticsService);
  private modalService = inject(NgbModal);
  private deleteService = inject(DeleteService);
  private auth = inject(AuthService);

  // ============================================
  // Org Methods
  // ============================================

  /**
   * Every org the current user has a relationship with (active + invited).
   * Served from the cached user payload (see AuthService) — no network call.
   * Call auth.refreshUser() after mutations that change org membership.
   */
  getOrgs(): Observable<OrgEntry[]> {
    return this.auth.$user.pipe(map((user) => user?.orgs ?? []));
  }

  /**
   * Get a single site by ID with full details
   */
  getOrg(orgId: string): Observable<OrgWithMembership> {
    return this.api
      .get<ApiSingleResponse<OrgWithMembership>>(`/my/orgs/${orgId}`)
      .pipe(map((res) => res.data));
  }

  /**
   * Get site credit balances
   */
  getSiteCredits(orgId: string): Observable<OrgCreditBalance[]> {
    return this.api
      .get<ApiListResponse<OrgCreditBalance>>(`/my/orgs/${orgId}/credits`)
      .pipe(map((res) => res.data));
  }

  /**
   * Update org settings (admin only). Pass only the fields you want to update.
   * Covers branding, subscription (seats/plan/expires/active), and core fields.
   */
  async updateOrg(orgId: string, updates: UpdateOrgRequest): Promise<OrgWithMembership> {
    const res = await firstValueFrom(
      this.api.put<ApiSingleResponse<OrgWithMembership>>(`/my/orgs/${orgId}`, updates),
    );
    return res.data as OrgWithMembership;
  }

  // ============================================
  // Org admins
  // ============================================

  getOrgAdmins(orgId: string): Observable<OrgAdmin[]> {
    return this.api
      .get<ApiListResponse<OrgAdmin>>(`/my/orgs/${orgId}/admins`)
      .pipe(map((res) => res.data));
  }

  async addOrgAdmin(
    orgId: string,
    email: string,
    displayName?: string | null,
  ): Promise<OrgAdmin> {
    const res = await firstValueFrom(
      this.api.post<ApiSingleResponse<OrgAdmin>>(
        `/my/orgs/${orgId}/admins`,
        { email, display_name: displayName ?? null },
      ),
    );
    return res.data as OrgAdmin;
  }

  /**
   * Remove an org admin. The admin row carries `status` and either user_id
   * (active) or invite id (pending) — route accordingly.
   */
  async removeOrgAdmin(orgId: string, admin: OrgAdmin): Promise<void> {
    const path = admin.status === 'active'
      ? `/my/orgs/${orgId}/admins/active/${admin.user_id}`
      : `/my/orgs/${orgId}/admins/pending/${admin.id}`;
    await firstValueFrom(this.api.delete(path));
  }

  // ============================================
  // Org collaborators (external admins / grants)
  // ============================================

  getOrgCollaborators(orgId: string): Observable<OrgCollaborator[]> {
    return this.api
      .get<ApiListResponse<OrgCollaborator>>(`/my/orgs/${orgId}/collaborators`)
      .pipe(map((res) => res.data));
  }

  /**
   * Grant an existing personal account admin authority over this org. The API
   * rejects emails with no account, org accounts, and supers.
   */
  async addOrgCollaborator(orgId: string, email: string): Promise<OrgCollaborator> {
    const res = await firstValueFrom(
      this.api.post<ApiSingleResponse<OrgCollaborator>>(
        `/my/orgs/${orgId}/collaborators`,
        { email },
      ),
    );
    return res.data as OrgCollaborator;
  }

  async removeOrgCollaborator(orgId: string, collaborator: OrgCollaborator): Promise<void> {
    await firstValueFrom(
      this.api.delete(`/my/orgs/${orgId}/collaborators/${collaborator.user_id}`),
    );
  }

  // ============================================
  // Org staff (people)
  // ============================================

  getOrgStaff(orgId: string): Observable<OrgStaff[]> {
    return this.api
      .get<ApiListResponse<OrgStaff>>(`/my/orgs/${orgId}/staff`)
      .pipe(map((res) => res.data));
  }

  async addOrgStaff(
    orgId: string,
    email: string,
    displayName?: string | null,
    firstName?: string | null,
    lastName?: string | null,
    options?: { password?: string | null },
  ): Promise<OrgStaff> {
    const res = await firstValueFrom(
      this.api.post<ApiSingleResponse<OrgStaff>>(
        `/my/orgs/${orgId}/staff`,
        {
          email,
          display_name: displayName ?? null,
          first_name: firstName ?? null,
          last_name: lastName ?? null,
          password: options?.password ?? null,
        },
      ),
    );
    return res.data as OrgStaff;
  }

  /**
   * List assume-session audit entries for the org (admin only).
   */
  getAssumeAuditLog(orgId: string): Observable<AssumeAuditEntry[]> {
    return this.api
      .get<ApiListResponse<AssumeAuditEntry>>(`/my/orgs/${orgId}/audit/assume`)
      .pipe(map((res) => res.data));
  }

  /**
   * Generate a one-time magic link the org admin can use to sign in as the
   * target staff member. Caller is responsible for opening the link (typically
   * in a new tab so the admin's own session is preserved).
   */
  async assumeStaff(orgId: string, userId: string): Promise<{ action_link: string; email: string }> {
    const res = await firstValueFrom(
      this.api.post<ApiSingleResponse<{ action_link: string; email: string }>>(
        `/my/orgs/${orgId}/staff/${userId}/assume`,
        {},
      ),
    );
    return res.data;
  }

  async removeOrgStaff(orgId: string, person: OrgStaff): Promise<void> {
    let path: string;
    if (person.status === 'active') {
      path = `/my/orgs/${orgId}/staff/active/${person.user_id}`;
    } else if (person.status === 'signup') {
      // Rejecting a pending self-signup bans the account (the org owns it).
      path = `/my/orgs/${orgId}/signups/${person.user_id}`;
    } else {
      path = `/my/orgs/${orgId}/staff/pending/${person.id}`;
    }
    await firstValueFrom(this.api.delete(path));
  }

  /** Classify a pending self-signup as org staff (teacher). */
  async approveSignupAsStaff(orgId: string, userId: string): Promise<void> {
    await firstValueFrom(
      this.api.post(`/my/orgs/${orgId}/signups/${userId}/approve-staff`, {}),
    );
  }

  // (Re-hiring a removed account goes through approveSignupAsStaff /
  // approveSignupAsStudent — same review dialog as pending signups.)

  /** Email a password-reset link to an org-owned account (staff or
   *  student). Server enforces: verified domain + not SSO-only. */
  async sendPasswordReset(orgId: string, userId: string): Promise<void> {
    await firstValueFrom(
      this.api.post(`/my/orgs/${orgId}/users/${userId}/password-reset`, {}),
    );
  }

  // ============================================
  // Offboarding: content transfer
  // ============================================

  /** What a (typically removed) teacher owns — counts for the modal. */
  async getStaffContentSummary(
    orgId: string,
    userId: string,
  ): Promise<{ classes: number; planners: number }> {
    const res = await firstValueFrom(
      this.api.get<ApiSingleResponse<{ classes: number; planners: number }>>(
        `/my/orgs/${orgId}/staff/${userId}/content-summary`,
      ),
    );
    return res.data;
  }

  /** Read-only deep view of one org member — account, classes, planners,
   *  documents, recent activity, AI usage. Powers the drill-into-a-user
   *  page in /admin and /super. */
  getUserDetail(orgId: string, userId: string): Observable<UserDetail> {
    return this.api
      .get<ApiSingleResponse<UserDetail>>(
        `/my/orgs/${orgId}/staff/${userId}/detail`,
      )
      .pipe(map((res) => res.data));
  }

  /** Reassign all of a teacher's classes + planners to another active
   *  org teacher. Returns moved counts. */
  async transferStaffContent(
    orgId: string,
    fromUserId: string,
    toUserId: string,
  ): Promise<{ classes: number; planners: number }> {
    const res = await firstValueFrom(
      this.api.post<ApiSingleResponse<{ classes: number; planners: number }>>(
        `/my/orgs/${orgId}/staff/${fromUserId}/transfer`,
        { to_user_id: toUserId },
      ),
    );
    return res.data;
  }

  // ============================================
  // Org students
  // ============================================

  getOrgStudents(orgId: string): Observable<OrgStudent[]> {
    return this.api
      .get<ApiListResponse<OrgStudent>>(`/my/orgs/${orgId}/students`)
      .pipe(map((res) => res.data));
  }

  /** Add a student by email. Existing account → classified directly;
   *  no account → student invite, claimed at signup. */
  async addOrgStudent(
    orgId: string,
    email: string,
    displayName?: string | null,
    firstName?: string | null,
    lastName?: string | null,
    campusId?: string | null,
    gradeLevel?: string | null,
    options?: { password?: string | null },
  ): Promise<OrgStudent> {
    const res = await firstValueFrom(
      this.api.post<ApiSingleResponse<OrgStudent>>(`/my/orgs/${orgId}/students`, {
        email,
        display_name: displayName ?? null,
        first_name: firstName ?? null,
        last_name: lastName ?? null,
        campus_id: campusId ?? null,
        grade_level: gradeLevel ?? null,
        password: options?.password ?? null,
      }),
    );
    return res.data as OrgStudent;
  }

  /** Cancel a pending student invite (same org_invite table as staff). */
  async cancelStudentInvite(orgId: string, inviteId: string): Promise<void> {
    await firstValueFrom(this.api.delete(`/my/orgs/${orgId}/staff/pending/${inviteId}`));
  }

  /** Update a student: campus assignment and/or grade level. Only the
   *  keys present in `changes` are touched. */
  async updateStudent(
    orgId: string,
    userId: string,
    changes: { campus_id?: string | null; grade_level?: string | null },
  ): Promise<void> {
    await firstValueFrom(
      this.api.put(`/my/orgs/${orgId}/students/${userId}`, changes),
    );
  }

  /** Remove a student: campus cleared, account disabled + banned. */
  async removeOrgStudent(orgId: string, userId: string): Promise<void> {
    await firstValueFrom(this.api.delete(`/my/orgs/${orgId}/students/${userId}`));
  }

  /** Classify a pending self-signup as an org student (optional campus). */
  async approveSignupAsStudent(
    orgId: string,
    userId: string,
    campusId?: string | null,
  ): Promise<void> {
    await firstValueFrom(
      this.api.post(`/my/orgs/${orgId}/signups/${userId}/approve-student`, {
        campus_id: campusId ?? null,
      }),
    );
  }

  /**
   * Campus assignments for a single person. For active org_staff: fetched
   * from campus_staff. For pending invites: read directly from the invite
   * row via getOrgStaff (caller passes the row).
   */
  async getActiveStaffCampuses(orgId: string, userId: string): Promise<string[]> {
    const res = await firstValueFrom(
      this.api.get<ApiSingleResponse<{ campus_ids: string[] }>>(
        `/my/orgs/${orgId}/staff/${userId}/campuses`,
      ),
    );
    return res.data?.campus_ids ?? [];
  }

  /**
   * Replace the campus assignments for an active org member. The endpoint
   * computes the diff and adds/removes campus_staff rows.
   */
  async setActiveStaffCampuses(
    orgId: string,
    userId: string,
    campusIds: string[],
  ): Promise<void> {
    await firstValueFrom(
      this.api.put<ApiSingleResponse<unknown>>(
        `/my/orgs/${orgId}/staff/${userId}/campuses`,
        { campus_ids: campusIds },
      ),
    );
  }

  /**
   * Replace the pre-assigned campus_ids on a pending org_invite. Applied to
   * the user as campus_staff rows when they accept the invite.
   */
  async setInviteCampuses(
    orgId: string,
    inviteId: string,
    campusIds: string[],
  ): Promise<void> {
    await firstValueFrom(
      this.api.put<ApiSingleResponse<unknown>>(
        `/my/orgs/${orgId}/invites/${inviteId}/campuses`,
        { campus_ids: campusIds },
      ),
    );
  }

  // ============================================
  // Pending invites for the current user
  // ============================================
  // The list lives on user.orgs (status='invited'). These mutate.

  async acceptInvite(orgId: string): Promise<void> {
    await firstValueFrom(
      this.api.post<ApiSingleResponse<unknown>>(`/my/invites/${orgId}/accept`, {}),
    );
    // Joining an org changes the user's org membership — refresh the
    // cached user payload so /admin and sidebar reflect it.
    await this.auth.refreshUser();
  }

  async declineInvite(orgId: string): Promise<void> {
    await firstValueFrom(
      this.api.post<ApiSingleResponse<unknown>>(`/my/invites/${orgId}/decline`, {}),
    );
    await this.auth.refreshUser();
  }

  // ---- Pending collaborator grants (the grantee's side) ----
  // These ride user.orgs as status='invited', role='collaborator', carrying
  // grant_id. Accepting confers org-admin authority via the grant.

  async acceptCollaboratorGrant(grantId: string): Promise<void> {
    await firstValueFrom(
      this.api.post<ApiSingleResponse<unknown>>(`/my/collaborations/${grantId}/accept`, {}),
    );
    await this.auth.refreshUser();
  }

  async declineCollaboratorGrant(grantId: string): Promise<void> {
    await firstValueFrom(
      this.api.delete<ApiSingleResponse<unknown>>(`/my/collaborations/${grantId}`),
    );
    await this.auth.refreshUser();
  }

  /**
   * Flip a person's org-admin status. Works for both active org_staff and
   * pending org_invite rows — the underlying endpoints route on status.
   */
  async setOrgAdminStatus(
    orgId: string,
    person: OrgStaff,
    makeAdmin: boolean,
  ): Promise<void> {
    if (makeAdmin) {
      if (!person.email) {
        throw new Error('Cannot promote: no email on this person');
      }
      // POST /admins handles both cases (existing user → user_account
      // is_org_admin flag, pending invite → org_invite.is_admin=true).
      await this.addOrgAdmin(orgId, person.email, person.display_name);
      return;
    }
    // Revoke: synthesize the OrgAdmin shape removeOrgAdmin expects.
    // 'signup' rows never carry admin status, so the narrowing is safe.
    await this.removeOrgAdmin(orgId, {
      id: person.id,
      user_id: person.user_id,
      email: person.email ?? '',
      display_name: person.display_name,
      status: person.status === 'pending' ? 'pending' : 'active',
      added_at: person.added_at,
    });
  }

  // ============================================
  // Org pages
  // ============================================

  // Reads stay observables — they're well suited to resource()/template async.
  getOrgPages(orgId: string): Observable<CampusPageSummary[]> {
    return this.api
      .get<ApiListResponse<CampusPageSummary>>(`/my/orgs/${orgId}/pages`)
      .pipe(map((res) => res.data));
  }

  getOrgPage(orgId: string, pageId: string): Observable<TeamPage> {
    return this.api
      .get<ApiSingleResponse<TeamPage>>(`/my/orgs/${orgId}/pages/${pageId}`)
      .pipe(map((res) => res.data));
  }

  // Mutations are promises.
  async createOrgPage(orgId: string, body: CreateCampusPageRequest): Promise<TeamPage> {
    const res = await firstValueFrom(
      this.api.post<ApiSingleResponse<TeamPage>>(`/my/orgs/${orgId}/pages`, body),
    );
    return res.data as TeamPage;
  }

  async updateOrgPage(
    orgId: string,
    pageId: string,
    body: UpdateCampusPageRequest,
  ): Promise<TeamPage> {
    const res = await firstValueFrom(
      this.api.put<ApiSingleResponse<TeamPage>>(`/my/orgs/${orgId}/pages/${pageId}`, body),
    );
    return res.data as TeamPage;
  }

  async deleteOrgPage(orgId: string, pageId: string): Promise<void> {
    await firstValueFrom(
      this.api.delete(`/my/orgs/${orgId}/pages/${pageId}`),
    );
  }

  async reorderOrgPages(orgId: string, pageIds: string[]): Promise<void> {
    await firstValueFrom(
      this.api.post<ApiSingleResponse<{ reordered: boolean }>>(
        `/my/orgs/${orgId}/pages/reorder`,
        { page_ids: pageIds },
      ),
    );
  }

  // ============================================
  // Campus Methods
  // ============================================

  /**
   * Every campus the current user can see — across all orgs. Org members
   * see only their campus_staff assignments; org admins see every campus
   * in their orgs.
   *
   * Served from the cached user payload (see AuthService) — no network call.
   * Call auth.refreshUser() after mutations that change membership.
   */
  getAllMyCampuses(): Observable<CampusEntry[]> {
    return this.auth.$user.pipe(map((user) => user?.campuses ?? []));
  }

  /**
   * Get all teams in a site
   */
  getCampuses(orgId: string): Observable<TeamSummary[]> {
    return this.api
      .get<ApiListResponse<TeamSummary>>(`/my/orgs/${orgId}/campuses`)
      .pipe(map((res) => res.data));
  }

  /**
   * Get a single team by ID (without orgId - uses direct route)
   */
  getCampusDirect(campusId: string): Observable<CampusWithDetails & { site: { org_id: string; name: string; slug: string; handle: string } }> {
    return this.api
      .get<ApiSingleResponse<CampusWithDetails & { site: { org_id: string; name: string; slug: string; handle: string } }>>(`/my/campuses/${campusId}`)
      .pipe(map((res) => res.data));
  }

  /**
   * Get a single team by ID (legacy - requires orgId)
   * @deprecated Use getCampusDirect instead
   */
  getCampus(orgId: string, campusId: string): Observable<CampusWithDetails> {
    return this.api
      .get<ApiSingleResponse<CampusWithDetails>>(`/my/orgs/${orgId}/campuses/${campusId}`)
      .pipe(map((res) => res.data));
  }

  /**
   * Create a new team (site admin only)
   */
  async createCampus(orgId: string, request: CreateCampusRequest): Promise<Campus> {
    const res = await firstValueFrom(
      this.api.post<ApiSingleResponse<Campus>>(`/my/orgs/${orgId}/campuses`, request)
    );
    this.analyticsService.capture('team_created', { org_id: orgId });
    await this.auth.refreshUser();
    return res.data;
  }

  /**
   * Update a team (team admin or site admin)
   */
  async updateCampus(
    orgId: string,
    campusId: string,
    request: UpdateCampusRequest
  ): Promise<Campus> {
    const res = await firstValueFrom(
      this.api.put<ApiSingleResponse<Campus>>(`/my/orgs/${orgId}/campuses/${campusId}`, request)
    );
    this.analyticsService.capture('team_updated', { org_id: orgId, campus_id: campusId });
    await this.auth.refreshUser();
    return res.data;
  }

  /**
   * Delete a team (site admin only)
   */
  async deleteCampus(orgId: string, campusId: string): Promise<void> {
    await firstValueFrom(
      this.api.delete<void>(`/my/orgs/${orgId}/campuses/${campusId}`)
    );
    this.analyticsService.capture('team_deleted', { org_id: orgId, campus_id: campusId });
    await this.auth.refreshUser();
  }

  /**
   * Reorder campuses in an org (org admin only). Persists the new
   * `sort_order` for each campus_id in the supplied array.
   */
  async reorderCampuses(orgId: string, campusIds: string[]): Promise<void> {
    await firstValueFrom(
      this.api.post(`/my/orgs/${orgId}/campuses/reorder`, { campus_ids: campusIds })
    );
  }

  /**
   * Delete a team with confirmation
   */
  async deleteTeamWithConfirm(orgId: string, team: TeamSummary): Promise<boolean> {
    const confirmed = await this.deleteService.confirmDelete(team.name);
    if (confirmed) {
      await this.deleteCampus(orgId, team.campus_id);
      return true;
    }
    return false;
  }

  // ============================================
  // Campus Member Methods (Direct - no orgId needed)
  // ============================================

  /**
   * Get all members of a team (direct route)
   */
  getCampusStaffDirect(campusId: string): Observable<CampusStaff[]> {
    return this.api
      .get<ApiListResponse<CampusStaff>>(`/my/campuses/${campusId}/members`)
      .pipe(map((res) => res.data));
  }

  /**
   * Add a member to a team (direct route)
   */
  async addTeamMemberDirect(
    campusId: string,
    request: AddTeamMemberRequest
  ): Promise<CampusStaff> {
    const res = await firstValueFrom(
      this.api.post<ApiSingleResponse<CampusStaff>>(
        `/my/campuses/${campusId}/members`,
        request
      )
    );
    this.analyticsService.capture('team_member_added', { campus_id: campusId });
    // Refresh in case the actor added themselves — keeps user.teams current.
    await this.auth.refreshUser();
    return res.data as CampusStaff;
  }

  /**
   * Update a team member's role (direct route)
   */
  async updateTeamMemberDirect(
    campusId: string,
    memberId: string,
    request: UpdateTeamMemberRequest
  ): Promise<CampusStaff> {
    const res = await firstValueFrom(
      this.api.put<ApiSingleResponse<CampusStaff>>(
        `/my/campuses/${campusId}/members/${memberId}`,
        request
      )
    );
    this.analyticsService.capture('team_member_updated', { campus_id: campusId });
    await this.auth.refreshUser();
    return res.data as CampusStaff;
  }

  /**
   * Remove a campus_staff row (direct route). `userId` is the auth.users id —
   * campus assignment is keyed on (campus_id, user_id) under Model C.
   */
  async removeTeamMemberDirect(campusId: string, userId: string): Promise<void> {
    await firstValueFrom(
      this.api.delete<void>(`/my/campuses/${campusId}/members/${userId}`)
    );
    this.analyticsService.capture('team_member_removed', { campus_id: campusId });
    await this.auth.refreshUser();
  }

  async removeTeamMemberWithConfirmDirect(
    campusId: string,
    member: CampusStaff
  ): Promise<boolean> {
    const label = member.display_name ?? member.email ?? 'this person';
    const confirmed = await this.deleteService.confirmDelete(label);
    if (confirmed) {
      await this.removeTeamMemberDirect(campusId, member.user_id);
      return true;
    }
    return false;
  }

  // ============================================
  // Campus Member Methods (Legacy - requires orgId)
  // ============================================

  /**
   * Get all members of a team
   * @deprecated Use getCampusStaffDirect instead
   */
  getCampusStaff(orgId: string, campusId: string): Observable<CampusStaff[]> {
    return this.api
      .get<ApiListResponse<CampusStaff>>(`/my/orgs/${orgId}/campuses/${campusId}/members`)
      .pipe(map((res) => res.data));
  }

  /**
   * Add a member to a team
   * @deprecated Use addTeamMemberDirect instead
   */
  async addCampusStaff(
    orgId: string,
    campusId: string,
    request: AddTeamMemberRequest
  ): Promise<CampusStaff> {
    const res = await firstValueFrom(
      this.api.post<ApiSingleResponse<CampusStaff>>(
        `/my/orgs/${orgId}/campuses/${campusId}/members`,
        request
      )
    );
    this.analyticsService.capture('team_member_added', {
      org_id: orgId,
      campus_id: campusId,
    });
    await this.auth.refreshUser();
    return res.data as CampusStaff;
  }

  /**
   * Update a team member's role
   * @deprecated Use updateTeamMemberDirect instead
   */
  async updateCampusStaff(
    orgId: string,
    campusId: string,
    memberId: string,
    request: UpdateTeamMemberRequest
  ): Promise<CampusStaff> {
    const res = await firstValueFrom(
      this.api.put<ApiSingleResponse<CampusStaff>>(
        `/my/orgs/${orgId}/campuses/${campusId}/members/${memberId}`,
        request
      )
    );
    this.analyticsService.capture('team_member_updated', {
      org_id: orgId,
      campus_id: campusId,
    });
    await this.auth.refreshUser();
    return res.data as CampusStaff;
  }

  /**
   * Remove a member from a team
   * @deprecated Use removeTeamMemberDirect instead
   */
  async removeCampusStaff(orgId: string, campusId: string, memberId: string): Promise<void> {
    await firstValueFrom(
      this.api.delete<void>(`/my/orgs/${orgId}/campuses/${campusId}/members/${memberId}`)
    );
    this.analyticsService.capture('team_member_removed', {
      org_id: orgId,
      campus_id: campusId,
    });
    await this.auth.refreshUser();
  }

  /**
   * Remove a team member with confirmation
   * @deprecated Use removeTeamMemberWithConfirmDirect instead
   */
  async removeTeamMemberWithConfirm(
    orgId: string,
    campusId: string,
    member: CampusStaff
  ): Promise<boolean> {
    const label = member.display_name ?? member.email ?? 'this person';
    const confirmed = await this.deleteService.confirmDelete(label);
    if (confirmed) {
      await this.removeCampusStaff(orgId, campusId, member.user_id);
      return true;
    }
    return false;
  }

  // ============================================
  // Campus Page Methods (Direct - no orgId needed)
  // ============================================

  /**
   * Get all pages for a team (direct route)
   */
  getCampusPagesDirect(campusId: string): Observable<CampusPageSummary[]> {
    return this.api
      .get<ApiListResponse<CampusPageSummary>>(`/my/campuses/${campusId}/pages`)
      .pipe(map((res) => res.data ?? []));
  }

  /**
   * Get all planners for classes in a team, grouped by teacher (direct route)
   */
  getCampusPlannersDirect(campusId: string): Observable<CampusPlannersResponse> {
    return this.api
      .get<ApiSingleResponse<CampusPlannersResponse>>(`/my/campuses/${campusId}/planners`)
      .pipe(map((res) => res.data));
  }

  /**
   * Get a single team page with full content (direct route)
   */
  getCampusPageDirect(campusId: string, pageId: string): Observable<TeamPage> {
    return this.api
      .get<ApiSingleResponse<TeamPage>>(`/my/campuses/${campusId}/pages/${pageId}`)
      .pipe(map((res) => res.data as TeamPage));
  }

  /**
   * Create a new team page (direct route)
   */
  async createTeamPageDirect(
    campusId: string,
    request: CreateCampusPageRequest
  ): Promise<TeamPage> {
    const res = await firstValueFrom(
      this.api.post<ApiSingleResponse<TeamPage>>(
        `/my/campuses/${campusId}/pages`,
        request
      )
    );
    return res.data as TeamPage;
  }

  /**
   * Update a team page (direct route)
   */
  async updateCampusPageDirect(
    campusId: string,
    pageId: string,
    request: UpdateCampusPageRequest
  ): Promise<TeamPage> {
    const res = await firstValueFrom(
      this.api.put<ApiSingleResponse<TeamPage>>(
        `/my/campuses/${campusId}/pages/${pageId}`,
        request
      )
    );
    return res.data as TeamPage;
  }

  /**
   * Delete a team page (direct route)
   */
  async deleteCampusPageDirect(campusId: string, pageId: string): Promise<void> {
    await firstValueFrom(
      this.api.delete(`/my/campuses/${campusId}/pages/${pageId}`)
    );
  }

  /**
   * Reorder team pages (direct route)
   */
  async reorderCampusPagesDirect(campusId: string, pageIds: string[]): Promise<void> {
    await firstValueFrom(
      this.api.post(`/my/campuses/${campusId}/pages/reorder`, { page_ids: pageIds })
    );
  }

  // ============================================
  // Campus Page Methods (Legacy - requires orgId)
  // ============================================

  /**
   * Get all pages for a team (summary list)
   * @deprecated Use getCampusPagesDirect instead
   */
  getCampusPages(orgId: string, campusId: string): Observable<CampusPageSummary[]> {
    return this.api
      .get<ApiListResponse<CampusPageSummary>>(
        `/my/orgs/${orgId}/campuses/${campusId}/pages`
      )
      .pipe(map((res) => res.data ?? []));
  }

  /**
   * Get a single team page with full content
   * @deprecated Use getCampusPageDirect instead
   */
  getCampusPage(orgId: string, campusId: string, pageId: string): Observable<TeamPage> {
    return this.api
      .get<ApiSingleResponse<TeamPage>>(
        `/my/orgs/${orgId}/campuses/${campusId}/pages/${pageId}`
      )
      .pipe(map((res) => res.data as TeamPage));
  }

  /**
   * Create a new team page
   * @deprecated Use createTeamPageDirect instead
   */
  async createCampusPage(
    orgId: string,
    campusId: string,
    request: CreateCampusPageRequest
  ): Promise<TeamPage> {
    const res = await firstValueFrom(
      this.api.post<ApiSingleResponse<TeamPage>>(
        `/my/orgs/${orgId}/campuses/${campusId}/pages`,
        request
      )
    );
    return res.data as TeamPage;
  }

  /**
   * Update a team page
   * @deprecated Use updateCampusPageDirect instead
   */
  async updateCampusPage(
    orgId: string,
    campusId: string,
    pageId: string,
    request: UpdateCampusPageRequest
  ): Promise<TeamPage> {
    const res = await firstValueFrom(
      this.api.put<ApiSingleResponse<TeamPage>>(
        `/my/orgs/${orgId}/campuses/${campusId}/pages/${pageId}`,
        request
      )
    );
    return res.data as TeamPage;
  }

  /**
   * Delete a team page
   * @deprecated Use deleteCampusPageDirect instead
   */
  async deleteCampusPage(orgId: string, campusId: string, pageId: string): Promise<void> {
    await firstValueFrom(
      this.api.delete(`/my/orgs/${orgId}/campuses/${campusId}/pages/${pageId}`)
    );
  }

  /**
   * Reorder team pages
   */
  async reorderTeamPages(
    orgId: string,
    campusId: string,
    pageIds: string[]
  ): Promise<void> {
    await firstValueFrom(
      this.api.post(`/my/orgs/${orgId}/campuses/${campusId}/pages/reorder`, {
        page_ids: pageIds,
      })
    );
  }
}
